import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/useAppDispatch";
import { Button, Form, Input, Modal, Radio, Spin } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { fetchInprogressAudios, fetchRefreshInProgress, setIsEditedForDocumentInfoModal, updatePack } from "../../../redux/JobModule/InProgressPage/InProgressAction";
import { formatJobNumber } from "../../../utils/formatters";

interface DocumentInfoModalProps {
  close: () => void;
  openState: boolean;
  open: () => void;
}

function DocumentInfoModal(props: DocumentInfoModalProps) {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);

  //Getting each pack Details
  const packDetails = useSelector((state: RootState) => state.InProgressAudiosState.packState.data);

  const Edited = useSelector((state: RootState) => state.InProgressAudiosState.packState.isEdited);

  // Loading state from redux slice
  const loading = useSelector((state: RootState) => state.InProgressAudiosState.packState.loading);
  //Populating the Form
  useEffect(() => {
    if (packDetails) {
      form.setFieldsValue({
        JobNumber: formatJobNumber(packDetails.jobNumber),
        Requestor: packDetails.clientName,
        Organization: packDetails.tenant,
        Priority: packDetails.packPriority,
      });
    } else {
      form.resetFields();
      console.warn("tenantDetails is undefined or null");
    }
  }, [packDetails, form]);

  //Modal Close Reseting the Fields
  const handleModalClose = () => {
    props.close();
    form.resetFields();
    setError(null);
  };

  const DocumentModalInfoContent = useMemo(() => {
    const onFinish = async (value: any) => {
      // Prepare details object to be saved
      // Convert spaces to underscores for backend storage
      const jobNumber = value.JobNumber?.replace(/\s+/g, "_");
      const details = {
        ...packDetails,
        jobNumber: jobNumber,
        clientName: value.Requestor,
        tenant: packDetails.tenant,
        packPriority: packDetails.packPriority,
      };
      await dispatch(updatePack({ packDetail: details, idPack: details.idPack }));
      handleModalClose();
      await dispatch(fetchRefreshInProgress({}));
    };

    return (
      <div className="d-flex flex-column gap-4 w-100 modal-body-content w-100">
        <h4> Edit Job</h4>

        <Form
          form={form}
          colon={false}
          name="documentInfoForm"
          layout="vertical"
          // labelCol={{ span: 8 }}
          // wrapperCol={{ span: 20 }}
          onFinish={onFinish}
          onValuesChange={() => {
            dispatch(setIsEditedForDocumentInfoModal(true));
          }}>
          <Form.Item
            className="form-item"
            label="Job Number"
            name="JobNumber"
            labelAlign="left"
            normalize={(value) => {
              return value
                .replace(/[^a-zA-Z0-9 ]/g, "")
                .replace(/^\s+/, "")
                .replace(/\s+/g, " ")
                .slice(0, 40);
            }}
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Please provide a valid Job Number.",
              },
              {
                max: 40,
                message: "Job Number cannot exceed 40 characters.",
              },
            ]}>
            <Input
              placeholder="Enter Job Number"
              onChange={(e) => {
                const val = e.target.value;
                if (val && /[^a-zA-Z0-9 ]/.test(val.slice(-1))) {
                  setError("Special characters are restricted. Use only Alphanumeric characters.");
                  setTimeout(() => {
                    setError(null);
                  }, 5000);
                }
                if (val.length > 40) {
                  setError("Job Number cannot exceed 40 characters.");
                  setTimeout(() => {
                    setError(null);
                  }, 5000);
                }
              }}
              onBlur={() => {
                const currentValue = form.getFieldValue("JobNumber");
                if (currentValue) {
                  form.setFieldsValue({
                    JobNumber: currentValue.trim(),
                  });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            className="form-item"
            label="Requestor"
            name="Requestor"
            labelAlign="left"
            normalize={(value) => {
              return value
                .replace(/[^a-zA-Z0-9 ]/g, "")
                .replace(/^\s+/, "")
                .replace(/\s+/g, " ")
                .slice(0, 40);
            }}
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Please provide a valid Requestor.",
              },
              {
                max: 40,
                message: "Requestor cannot exceed 40 characters.",
              },
            ]}>
            <Input
              placeholder="Enter Requestor"
              onChange={(e) => {
                const val = e.target.value;
                if (val && /[^a-zA-Z0-9 ]/.test(val.slice(-1))) {
                  setError("Special characters are restricted. Use only Alphanumeric characters.");
                  setTimeout(() => {
                    setError(null);
                  }, 5000);
                }
                if (val.length > 40) {
                  setError("Requestor cannot exceed 40 characters.");
                  setTimeout(() => {
                    setError(null);
                  }, 5000);
                }
              }}
              onBlur={() => {
                const currentValue = form.getFieldValue("Requestor");
                if (currentValue) {
                  form.setFieldsValue({
                    Requestor: currentValue.trim(),
                  });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            className="form-item"
            label="Organization"
            name="Organization"
            labelAlign="left"
            rules={[
              {
                required: true,
                message: "Please provide Organization.",
              },
            ]}>
            <Input disabled />
          </Form.Item>

          <Form.Item
            className="form-item"
            label="Priority"
            name="Priority"
            labelAlign="left"
            rules={[{ required: true, message: "Please select an Priority." }]}>
            <Radio.Group disabled>
              <Radio value="Standard">Standard</Radio>
              <Radio value="Express">Express</Radio>
            </Radio.Group>
          </Form.Item>

          {error && (
            <div className="error justify-content-end align-self-end mb-2">
              {error}
            </div>
          )}

          <div className="d-flex gap-3 justify-content-end">
            <Button
              type="primary"
                className="button radius"
              disabled={Edited ? false : true}
              htmlType="submit">
              Save
            </Button>

            <Button
              className="cancelButton"
              htmlType="reset"
              onClick={handleModalClose}>
              Cancel
            </Button>
          </div>
        </Form>
      </div>
    );
  }, [form, packDetails, loading, Edited, error]);

  return (
    <div>
      <Modal
        centered
        zIndex={1500}
        footer={[]}
        loading={loading}
        width={400}
        onCancel={handleModalClose}
        open={props.openState}>
        {DocumentModalInfoContent}
      </Modal>
    </div>
  );
}

export default DocumentInfoModal;
